import * as GobodoApi from '@gobodo/gobodo-api-promise';
import GobodoConfig from '../../config/gobodoConfig';
import { getCSAuditAuditorRelKey, getCSAuditClientRelKey } from '../../config/gobodoAccountRelationshipKeys';
import { getGobodoClient } from '../../gobodo/gobodoClient';
import { isRetryableError } from '../../utils/gobodoErrorHandler';
import { retryWithBackoff } from '../../utils/gobodoRetry';
import { normalizeToGobodoFormat } from '../../utils/objectApiNameNormalizer';
import { convertFromGobodoRecord } from '../gobodo/gobodoRecordService';
import { getRecordById } from '../recordStorageService';
import { CACHE_TTL, makeCacheOpts, type GobodoCacheOpts } from '../gobodo/gobodoCacheConfig';
import { isGobodoGraphObjectKeyPlainUuid } from '../../utils/gobodoRecordKeySuffix';

const CSAUDIT_OBJECT = 'CSAudit__c';
const CLIENT_OBJECT = 'Client';
const CUSTOM_SUFFIX = process.env.REACT_APP_GOBODO_CUSTOM_SUFFIX ?? '__c';
const KNOWN_SUFFIXES = [CUSTOM_SUFFIX, '__stg3_c', '__stg_c', '__dev_c', '__c'];
const MY_WORK_CSAUDIT_TRAV_CLIENT_NAME_FIELD = `CSAuditClientTravName${CUSTOM_SUFFIX}`;
const MY_WORK_CSAUDIT_DATA_FIELDS = [
  'Name',
  'name',
  'Name__c',
  'Auditor__c',
  'auditor',
  'custom__auditor',
  'Phase_Status__c',
  'PhaseStatus__c',
  'phaseStatus',
  'custom__phase_status',
  'Due_Date__c',
  'DueDate__c',
  'dueDate',
  'custom__due_date',
  'AuditTypeDisplayName__c',
  'auditTypeDisplayName',
  'AuditTemplateName__c',
  'auditTemplateName',
  'Audit_Type__c',
  'AuditType__c',
  'auditType',
  'Account__c',
  'ClientId__c',
  'clientId',
  'relatedClientId',
  'custom__account',
  'ClientName__c',
  'AccountName__c',
  'Account_Name__c',
  'FirstName__c',
  'First_Name__c',
  'custom__first_name',
  'LastName__c',
  'Last_Name__c',
  'custom__last_name',
  MY_WORK_CSAUDIT_TRAV_CLIENT_NAME_FIELD,
].join(',');

type GobodoTraversalReadOpts = GobodoCacheOpts & {
  objectSections?: string;
  dataFields?: string;
  maxObjectTypeFields?: number;
  includeExpandedFieldData?: boolean;
  includeRelatedMedia?: boolean;
};

function buildTraversalProjectionOpts(
  cacheOpts: GobodoCacheOpts,
  dataFields: string
): GobodoTraversalReadOpts {
  return {
    ...cacheOpts,
    objectSections: 'data',
    dataFields,
    maxObjectTypeFields: 0,
    includeExpandedFieldData: false,
    includeRelatedMedia: false,
  };
}

/**
 * Gobodo index names on CSAudit for "assigned auditor" semantics. Each name becomes its own
 * zero-hop `GraphTraversalInput` branch and is unioned with the primary auditor-edge hop (see
 * {@link buildMyAuditsTraversalInput}). Add entries here when new auditor fields are indexed.
 */
export const MY_WORK_CSAUDIT_AUDITOR_FIELD_INDEX_NAMES: readonly string[] = ['Auditor__c', 'custom__auditor'];

function stableAuditorFieldIndexKey(auditorFieldIndexNames?: readonly string[]): string {
  const names = auditorFieldIndexNames?.length
    ? uniqueNonEmpty([...auditorFieldIndexNames])
    : [...MY_WORK_CSAUDIT_AUDITOR_FIELD_INDEX_NAMES];
  return [...names].sort().join('|');
}

/**
 * Gobodo server-side cache for My Work CSAudit traversal (relationship + union field branches).
 * Key parts must track graph id, user object key, SF user id, and which field-index branches are used.
 */
export function myWorkCSAuditsTraversalCacheOpts(
  graphUserKey: string,
  sfUserId: string,
  auditorFieldIndexNames?: readonly string[]
): GobodoCacheOpts {
  const gid = String(GobodoConfig.graph());
  return makeCacheOpts(
    [
      'cybersweep',
      'my-work',
      'cs-audit',
      'traverse',
      gid,
      cleanString(graphUserKey),
      cleanString(sfUserId),
      stableAuditorFieldIndexKey(auditorFieldIndexNames),
      'v2-projected',
    ],
    CACHE_TTL.SHORT,
    { group: ['cybersweep', 'my-work-cs-audits', gid] }
  );
}

/**
 * Server-side cache for My Work when only field-index branches run (no graph user start node).
 */
export function myWorkCSAuditsIndexOnlyTraversalCacheOpts(
  sfUserId: string,
  auditorFieldIndexNames?: readonly string[]
): GobodoCacheOpts {
  const gid = String(GobodoConfig.graph());
  return makeCacheOpts(
    [
      'cybersweep',
      'my-work',
      'cs-audit',
      'traverse-index-only',
      gid,
      cleanString(sfUserId),
      stableAuditorFieldIndexKey(auditorFieldIndexNames),
      'v2-projected',
    ],
    CACHE_TTL.SHORT,
    { group: ['cybersweep', 'my-work-cs-audits', gid] }
  );
}

function cleanString(value: unknown): string {
  return value == null ? '' : String(value).trim();
}

/**
 * Gobodo internal object ids are numeric. The traversal API expects {@link GraphTraversalInput.setStartObjectId}
 * for those — not `startObjectKey` with a digit string (see Credit Block activity traversals).
 */
function parseNumericGraphObjectId(raw: string): number | null {
  const value = cleanString(raw);
  if (!/^[0-9]+$/.test(value)) return null;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) ? parsed : null;
}

function applyStartObjectKeyOrId(
  input: InstanceType<typeof GobodoApi.GraphTraversalInput>,
  token: string
): void {
  const numericId = parseNumericGraphObjectId(token);
  if (numericId != null) {
    input.setStartObjectId(numericId);
  } else {
    input.setStartObjectKey(token);
  }
}

/** Dev log label for the traversal start (matches GraphTraversalInput setters). */
function describeTraversalStartToken(token: string): string {
  const id = parseNumericGraphObjectId(token);
  if (id != null) return `startObjectId=${id}`;
  return `startObjectKey=${token}`;
}

function stripKnownSuffix(value: string): string {
  const raw = cleanString(value);
  if (!raw) return '';
  for (const suffix of KNOWN_SUFFIXES) {
    if (suffix && raw.toLowerCase().endsWith(String(suffix).toLowerCase())) {
      return raw.slice(0, raw.length - String(suffix).length);
    }
  }
  return raw;
}

function uniqueNonEmpty(values: string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const value of values) {
    const cleaned = cleanString(value);
    if (!cleaned || seen.has(cleaned)) continue;
    seen.add(cleaned);
    unique.push(cleaned);
  }
  return unique;
}

export async function buildClientTraversalStartKeyCandidates(clientId: string): Promise<string[]> {
  const rawId = cleanString(clientId);
  if (!rawId) return [];

  const candidates: string[] = [];
  if (/^\d+$/.test(rawId)) {
    try {
      const clientRecord = await getRecordById(CLIENT_OBJECT, rawId);
      const resolvedKey = cleanString((clientRecord as Record<string, unknown> | null)?.key);
      if (resolvedKey) candidates.push(resolvedKey);
    } catch {
      // Continue with non-resolved candidates.
    }
    // Internal id: traversal uses `setStartObjectId` (numeric), not a digit string in `startObjectKey`.
    candidates.push(rawId);
    return uniqueNonEmpty(candidates);
  }

  /** App/portal–created graph rows often use a bare UUID as `key` (not `uuid__stg5_c`). */
  if (isGobodoGraphObjectKeyPlainUuid(rawId)) {
    return uniqueNonEmpty([rawId]);
  }

  const stripped = stripKnownSuffix(rawId);
  if (rawId.toLowerCase().endsWith(CUSTOM_SUFFIX.toLowerCase())) {
    candidates.push(rawId);
  } else if (stripped) {
    candidates.push(`${stripped}${CUSTOM_SUFFIX}`);
  }

  return uniqueNonEmpty(candidates);
}

export function buildCSAuditTraversalInput(startObjectKey: string): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  applyStartObjectKeyOrId(input, startObjectKey);
  input.setRelationshipTypes([getCSAuditClientRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([normalizeToGobodoFormat(CSAUDIT_OBJECT)]);
  input.setMaxPathLength(1);
  return input;
}

/**
 * Zero-hop CSAudit traversal: objects where `indexName` equals the Salesforce user id (e.g. 005…).
 * Used as a union branch alongside the auditor relationship hop when indexes exist on those fields.
 */
export function buildCSAuditFieldEqualsSfUserTraversalInput(
  sfUserId: string,
  fieldIndexName: string
): InstanceType<typeof GobodoApi.GraphTraversalInput> | null {
  const id = cleanString(sfUserId);
  const indexName = cleanString(fieldIndexName);
  if (!id || !indexName) return null;

  const csType = normalizeToGobodoFormat(CSAUDIT_OBJECT);
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  input.setStartObjectTypes([csType]);
  input.setEndObjectTypes([csType]);
  input.setMaxPathLength(0);
  try {
    const filter = new GobodoApi.IndexFilter();
    filter.setIndexName(indexName);
    filter.setFunction(GobodoApi.IndexFilter.FunctionEnum.EQUALS);
    filter.setValues([id]);
    input.setStartObjectFieldFilters([filter]);
  } catch {
    return null;
  }
  return input;
}

/**
 * Chains union traversals: `head.setUnionWith(next)`, then `next.setUnionWith(next)`, … (same idea as
 * Credit Block Case union: relationship branch OR field-index branch(es)).
 */
function chainUnionTraversals(
  head: InstanceType<typeof GobodoApi.GraphTraversalInput>,
  branches: Array<InstanceType<typeof GobodoApi.GraphTraversalInput>>
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  if (branches.length === 0) return head;
  let tail = head;
  for (const branch of branches) {
    tail.setUnionWith(branch);
    tail = branch;
  }
  return head;
}

export type BuildMyAuditsTraversalOptions = {
  /** Salesforce user id (15/18) for field-index union branches; omit to skip those branches. */
  sfUserId?: string;
  /** Index names for {@link buildCSAuditFieldEqualsSfUserTraversalInput}; defaults to {@link MY_WORK_CSAUDIT_AUDITOR_FIELD_INDEX_NAMES}. */
  auditorFieldIndexNames?: readonly string[];
};

/**
 * My Work audits: primary branch is a single-hop traversal from the user key via `cs_audit_auditor_rel`
 * (INCOMING). Optional union branch(es): zero-hop CSAudit traversals with `IndexFilter` EQUALS on
 * auditor fields (e.g. `Auditor__c`, `custom__auditor`) so rows match when the relationship edge is
 * missing but field indexes are populated. Multiple field branches are chained with nested
 * `setUnionWith` (OR semantics across primary + each branch).
 */
export function buildMyAuditsTraversalInput(
  userStartObjectKey: string,
  options?: BuildMyAuditsTraversalOptions
): InstanceType<typeof GobodoApi.GraphTraversalInput> {
  const input = new GobodoApi.GraphTraversalInput();
  input.setGraphId(GobodoConfig.graph());
  applyStartObjectKeyOrId(input, userStartObjectKey);
  input.setRelationshipTypes([getCSAuditAuditorRelKey()]);
  input.setDirection(GobodoApi.GraphTraversalInput.DirectionEnum.INCOMING);
  input.setEndObjectTypes([normalizeToGobodoFormat(CSAUDIT_OBJECT)]);
  input.setMaxPathLength(1);

  const sfId = cleanString(options?.sfUserId ?? '');
  const names = options?.auditorFieldIndexNames ?? MY_WORK_CSAUDIT_AUDITOR_FIELD_INDEX_NAMES;
  if (!sfId || names.length === 0) {
    return input;
  }

  const fieldBranches = names
    .map((name) => buildCSAuditFieldEqualsSfUserTraversalInput(sfId, name))
    .filter((b): b is InstanceType<typeof GobodoApi.GraphTraversalInput> => b != null);

  return chainUnionTraversals(input, fieldBranches);
}

/**
 * My Work without a resolved graph USER key: OR of zero-hop CSAudit traversals with
 * {@link IndexFilter} EQUALS on auditor fields (same branches as the union tail of
 * {@link buildMyAuditsTraversalInput}). Requires Gobodo indexes on those field names.
 */
export function buildMyAuditsIndexOnlyTraversalInput(
  sfUserId: string,
  auditorFieldIndexNames?: readonly string[]
): InstanceType<typeof GobodoApi.GraphTraversalInput> | null {
  const sfId = cleanString(sfUserId);
  const names = auditorFieldIndexNames?.length
    ? uniqueNonEmpty([...auditorFieldIndexNames])
    : [...MY_WORK_CSAUDIT_AUDITOR_FIELD_INDEX_NAMES];
  if (!sfId || names.length === 0) return null;

  const fieldBranches = names
    .map((name) => buildCSAuditFieldEqualsSfUserTraversalInput(sfId, name))
    .filter((b): b is InstanceType<typeof GobodoApi.GraphTraversalInput> => b != null);
  if (fieldBranches.length === 0) return null;
  return chainUnionTraversals(fieldBranches[0], fieldBranches.slice(1));
}

function dedupeCSAuditRecords(records: Record<string, unknown>[]): Record<string, unknown>[] {
  const seen = new Set<string>();
  const out: Record<string, unknown>[] = [];
  for (const r of records) {
    const k = cleanString(r.id ?? r.key ?? '');
    if (!k || seen.has(k)) continue;
    seen.add(k);
    out.push(r);
  }
  return out;
}

/**
 * Loads CS Audit records assigned to the given Gobodo user object key (portal USER / graph user key).
 * Pass `sfUserId` so the traversal can union relationship results with field-index branches (see
 * {@link buildMyAuditsTraversalInput}).
 */
export async function fetchMyCSAuditsViaTraversal(
  userStartObjectKey: string,
  sfUserId?: string,
  auditorFieldIndexNames?: readonly string[]
): Promise<Record<string, unknown>[]> {
  const key = cleanString(userStartObjectKey);
  if (!key) return [];

  const client = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(client);
  const sf = cleanString(sfUserId ?? '');
  const input = buildMyAuditsTraversalInput(key, {
    sfUserId: sf,
    auditorFieldIndexNames,
  });
  const cacheOpts = myWorkCSAuditsTraversalCacheOpts(key, sf, auditorFieldIndexNames);
  const readOpts = buildTraversalProjectionOpts(cacheOpts, MY_WORK_CSAUDIT_DATA_FIELDS);

  try {
    const result = await retryWithBackoff(
      () => traversalApi.traverseObjects(input, readOpts),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    const rawList = Array.isArray(result)
      ? result.map((gobodoObj: unknown) => convertFromGobodoRecord(gobodoObj, CSAUDIT_OBJECT) as Record<string, unknown>)
      : [];
    const converted = dedupeCSAuditRecords(rawList);
    if (process.env.NODE_ENV === 'development') {
      console.log('[MyAudits Traversal] count=', converted.length, 'startKey=', key);
    }
    return converted;
  } catch (error) {
    if (isRetryableError(error)) throw error;
    console.warn('[MyAudits Traversal] failed:', error);
    return [];
  }
}

/**
 * Loads CS Audits where auditor reference fields match the Salesforce user id via Gobodo field indexes
 * only (no `cs_audit_auditor_rel` hop, no USER start object). Use when the portal user graph object
 * is missing or the relationship traversal returned no rows but indexes are populated.
 */
export async function fetchMyCSAuditsViaAuditorFieldIndexes(
  sfUserId: string,
  auditorFieldIndexNames?: readonly string[]
): Promise<Record<string, unknown>[]> {
  const sf = cleanString(sfUserId);
  if (!sf) return [];

  const input = buildMyAuditsIndexOnlyTraversalInput(sf, auditorFieldIndexNames);
  if (!input) return [];

  const client = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(client);
  const cacheOpts = myWorkCSAuditsIndexOnlyTraversalCacheOpts(sf, auditorFieldIndexNames);
  const readOpts = buildTraversalProjectionOpts(cacheOpts, MY_WORK_CSAUDIT_DATA_FIELDS);

  try {
    const result = await retryWithBackoff(
      () => traversalApi.traverseObjects(input, readOpts),
      { maxRetries: 2, initialDelayMs: 1000 }
    );
    const rawList = Array.isArray(result)
      ? result.map((gobodoObj: unknown) => convertFromGobodoRecord(gobodoObj, CSAUDIT_OBJECT) as Record<string, unknown>)
      : [];
    const converted = dedupeCSAuditRecords(rawList);
    if (process.env.NODE_ENV === 'development') {
      console.log('[MyAudits Index-only] count=', converted.length, 'sfUserId=', sf);
    }
    return converted;
  } catch (error) {
    if (isRetryableError(error)) throw error;
    console.warn('[MyAudits Index-only] failed:', error);
    return [];
  }
}

/**
 * Extract the client reference from an audit record (checks multiple field names)
 */
function getAuditClientRef(audit: Record<string, unknown>): string {
  const data = (audit.data as Record<string, unknown>) ?? audit;
  return cleanString(
    audit.relatedClientId ?? audit.clientId ?? audit.ClientId__c ?? audit.Account__c ?? audit.custom__account ??
    data.relatedClientId ?? data.clientId ?? data.ClientId__c ?? data.Account__c ?? data.custom__account ?? ''
  );
}

export async function fetchClientCSAuditsViaTraversal(clientId: string): Promise<Record<string, unknown>[]> {
  console.group('[CSAudit Traversal] Fetching audits for client');
  console.log('Input clientId:', clientId);

  const startKeyCandidates = await buildClientTraversalStartKeyCandidates(clientId);
  console.log('Start key candidates:', startKeyCandidates);

  if (startKeyCandidates.length === 0) {
    console.warn('No start key candidates generated!');
    console.groupEnd();
    return [];
  }

  // Build a set of all valid key variants for this client (for validation)
  const expectedKeyVariants = new Set(startKeyCandidates.map(k => k.toLowerCase()));
  // Also add stripped versions
  startKeyCandidates.forEach(k => {
    const stripped = stripKnownSuffix(k);
    if (stripped) expectedKeyVariants.add(stripped.toLowerCase());
  });
  console.log('Expected key variants for validation:', Array.from(expectedKeyVariants));

  const client = getGobodoClient();
  const traversalApi = new GobodoApi.GraphTraversalApi(client);

  for (const startObjectKey of startKeyCandidates) {
    const input = buildCSAuditTraversalInput(startObjectKey);
    console.log('Trying', describeTraversalStartToken(startObjectKey));
    try {
      const result = await retryWithBackoff(
        () => traversalApi.traverseObjects(input),
        { maxRetries: 2, initialDelayMs: 1000 }
      );

      const converted = Array.isArray(result)
        ? result.map((gobodoObj: unknown) => convertFromGobodoRecord(gobodoObj, CSAUDIT_OBJECT) as Record<string, unknown>)
        : [];

      if (converted.length > 0) {
        console.log('Traversal returned', converted.length, 'audits with', describeTraversalStartToken(startObjectKey));

        // CRITICAL: Validate that each audit's client reference matches our expected client
        // This prevents returning audits that belong to a different client
        const validatedAudits = converted.filter(audit => {
          const auditClientRef = getAuditClientRef(audit);
          const refLower = auditClientRef.toLowerCase();
          const strippedRefLower = stripKnownSuffix(auditClientRef).toLowerCase();

          const isValid = expectedKeyVariants.has(refLower) || expectedKeyVariants.has(strippedRefLower);
          if (!isValid) {
            console.warn(`  FILTERED OUT audit ${audit.id}: clientRef="${auditClientRef}" does not match expected client`);
          }
          return isValid;
        });

        console.log('After validation:', validatedAudits.length, 'audits belong to this client');

        // Log sample of validated audits
        validatedAudits.slice(0, 3).forEach((audit, i) => {
          const auditClientRef = getAuditClientRef(audit);
          console.log(`  Audit ${i + 1}: id=${audit.id}, clientRef=${auditClientRef}, name=${audit.name ?? audit.label}`);
        });

        if (validatedAudits.length > 0) {
          console.groupEnd();
          return validatedAudits;
        }

        // If all audits were filtered out, continue to next candidate
        console.log('All audits filtered out for', describeTraversalStartToken(startObjectKey), '- trying next candidate');
      } else {
        console.log('No audits found with', describeTraversalStartToken(startObjectKey));
      }
    } catch (error) {
      if (isRetryableError(error)) {
        console.groupEnd();
        throw error;
      }
      console.warn('Traversal failed for', describeTraversalStartToken(startObjectKey), error);
    }
  }

  console.warn('No audits found for any candidate key');
  console.groupEnd();
  return [];
}
